import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { finalize, switchMap } from 'rxjs';
import { MyOskStudentAgreementService } from '../../../../api/api-services/my-osk-student-agreement.service';
import { MyOskStudentAgreementsService } from '../../../../api/api-services/my-osk-student-agreements.service';
import { IMyOskStudentAgreementVm } from '../../../../api/my-osk-student-agreement-vm.model';
import {
  AgreementPickerComponent,
  AgreementSelection,
} from '../../../shared/components/agreement-picker/agreement-picker.component';
import { agreementApiError } from '../../../shared/helpers/agreement-api-error';
import { AgreementPdfService } from '../../../shared/services/agreement-pdf.service';

/**
 * "Umowy" tab of the student details: agreements generated for the student (printed again exactly as
 * they were) and generating another one from the data stored for the student.
 */
@Component({
  selector: 'app-student-agreements',
  standalone: true,
  imports: [CommonModule, ButtonModule, ConfirmDialogModule, TooltipModule, AgreementPickerComponent],
  providers: [ConfirmationService],
  templateUrl: './student-agreements.component.html',
  styleUrl: './student-agreements.component.scss',
})
export class StudentAgreementsComponent implements OnChanges {
  @Input({ required: true }) studentGuid!: string;
  @Input() studentName: string | null | undefined = null;
  @Input() examCategoryGuid: string | null | undefined = null;

  private readonly studentAgreementsService = inject(MyOskStudentAgreementsService);
  private readonly studentAgreementService = inject(MyOskStudentAgreementService);
  private readonly agreementPdfService = inject(AgreementPdfService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);

  public agreements: IMyOskStudentAgreementVm[] = [];
  public loading = false;
  public generating = false;
  public pdfLoadingGuid: string | null = null;
  public selection: AgreementSelection = { agreementGuid: null, courseGuid: null };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['studentGuid'] && this.studentGuid) {
      this.selection = { agreementGuid: null, courseGuid: null };
      this.load();
    }
  }

  download(agreement: IMyOskStudentAgreementVm): void {
    if (this.pdfLoadingGuid) return;
    this.pdfLoadingGuid = agreement.guid;
    this.agreementPdfService
      .download(agreement.agreementName, agreement.content, [this.studentName])
      .pipe(finalize(() => (this.pdfLoadingGuid = null)))
      .subscribe({ error: () => this.showError('Nie udało się przygotować umowy PDF. Spróbuj ponownie.') });
  }

  /** Fills the chosen agreement with the student's current data, keeps it and downloads the PDF. */
  generate(): void {
    const agreementGuid = this.selection.agreementGuid;
    if (!agreementGuid || this.generating) return;

    this.generating = true;
    this.studentAgreementService
      .post({
        studentGuid: this.studentGuid,
        agreementGuid,
        courseGuid: this.selection.courseGuid ?? undefined,
      })
      .pipe(
        switchMap((agreement) => {
          this.agreements = [agreement, ...this.agreements];
          this.messageService.add({ severity: 'success', detail: 'Umowa została wygenerowana i zapisana.' });
          return this.agreementPdfService.download(agreement.agreementName, agreement.content, [this.studentName]);
        }),
        finalize(() => (this.generating = false))
      )
      .subscribe({ error: (error) => this.showError(agreementApiError(error, 'Nie udało się wygenerować umowy.')) });
  }

  remove(agreement: IMyOskStudentAgreementVm): void {
    this.confirmationService.confirm({
      key: 'studentAgreementConfirm',
      header: 'Usuwanie umowy kursanta',
      message: `Czy na pewno chcesz usunąć umowę „${agreement.agreementName}” z ${this.formatDate(agreement.created)}? Usuń ją tylko, jeśli została wygenerowana przez pomyłkę.`,
      acceptLabel: 'Usuń',
      rejectLabel: 'Anuluj',
      defaultFocus: 'reject',
      accept: () =>
        this.studentAgreementService.delete(agreement.guid).subscribe({
          next: () => {
            this.agreements = this.agreements.filter((item) => item.guid !== agreement.guid);
            this.messageService.add({ severity: 'success', detail: 'Umowa kursanta została usunięta.' });
          },
          error: (error) => this.showError(agreementApiError(error, 'Nie udało się usunąć umowy.')),
        }),
    });
  }

  private load(): void {
    this.loading = true;
    this.studentAgreementsService
      .get(this.studentGuid)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (agreements) => (this.agreements = agreements),
        error: () => this.showError('Nie udało się pobrać umów kursanta.'),
      });
  }

  private formatDate(value: Date): string {
    return new Date(value).toLocaleDateString('pl-PL');
  }

  private showError(detail: string): void {
    this.messageService.add({ severity: 'error', summary: 'Błąd', detail });
  }
}
